import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Sparkles, Check, X, Upload, Clock, FileText, Wand2, RefreshCw,
  Eye, RotateCcw, Zap, Calendar, Tag
} from 'lucide-react';
import { toast } from 'sonner';
import aiContentAPI from './aiContentApi';

const AIContentManager = () => {
  const [activeTab, setActiveTab] = useState('generate');
  const [loading, setLoading] = useState(false);
  const [pendingContent, setPendingContent] = useState([]);
  const [contentHistory, setContentHistory] = useState([]);
  const [generatedPreview, setGeneratedPreview] = useState(null);
  
  // Generation form state
  const [contentType, setContentType] = useState('homepage_hero');
  const [formData, setFormData] = useState({
    context: 'general',
    categoryId: '',
    categoryName: '',
    productId: '',
    promoType: '',
    targets: '',
    offer: '',
    pageName: '',
    contentSummary: ''
  });

  useEffect(() => {
    loadPendingContent();
    loadHistory();
  }, []);

  const loadPendingContent = async () => {
    try {
      const data = await aiContentAPI.getPendingContent();
      setPendingContent(data.pending_content || []);
    } catch (error) {
      console.error('Error loading pending content:', error);
    }
  };

  const loadHistory = async () => {
    try {
      const data = await aiContentAPI.getContentHistory(null, 20);
      setContentHistory(data.history || []);
    } catch (error) {
      console.error('Error loading history:', error);
    }
  };

  const handleGenerate = async () => {
    setLoading(true);
    setGeneratedPreview(null);
    
    try {
      let result;
      
      switch (contentType) {
        case 'homepage_hero':
          result = await aiContentAPI.generateHomepageHero(formData.context);
          break;
        case 'category_description':
          result = await aiContentAPI.generateCategoryDescription(
            formData.categoryId,
            formData.categoryName
          );
          break;
        case 'product_description':
          result = await aiContentAPI.enhanceProductDescription(formData.productId);
          break;
        case 'promotional_banner':
          result = await aiContentAPI.generatePromotionalBanner(
            formData.promoType,
            formData.targets,
            formData.offer
          );
          break;
        case 'seo_meta':
          result = await aiContentAPI.generateSeoMeta(
            formData.pageName,
            formData.contentSummary
          );
          break;
        case 'about_page':
          result = await aiContentAPI.generateAboutPage();
          break;
        case 'faq':
          result = await aiContentAPI.generateFaq();
          break;
        default:
          throw new Error('Invalid content type');
      }
      
      setGeneratedPreview(result);
      toast.success('Content generated successfully!');
      loadPendingContent();
    } catch (error) {
      console.error('Generation error:', error);
      toast.error(error.response?.data?.detail || 'Generation failed');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (contentId) => {
    try {
      await aiContentAPI.approveContent(contentId);
      toast.success('Content approved');
      loadPendingContent();
      loadHistory();
    } catch (error) {
      toast.error('Failed to approve');
    }
  };

  const handleReject = async (contentId) => {
    try {
      await aiContentAPI.rejectContent(contentId, 'Rejected by admin');
      toast.success('Content rejected');
      loadPendingContent();
      loadHistory();
    } catch (error) {
      toast.error('Failed to reject');
    }
  };

  const handlePublish = async (contentId) => {
    try {
      await aiContentAPI.publishContent(contentId);
      toast.success('Content published!');
      loadPendingContent();
      loadHistory();
    } catch (error) {
      toast.error('Failed to publish. Content must be approved first.');
    }
  };

  const handleTriggerDaily = async () => {
    try {
      await aiContentAPI.triggerDailyUpdates();
      toast.success('Daily updates scheduled');
    } catch (error) {
      toast.error('Failed to trigger updates');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-800',
      approved: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800',
      published: 'bg-blue-100 text-blue-800',
      reverted: 'bg-gray-100 text-gray-800'
    };
    return <Badge className={styles[status] || 'bg-gray-100'}>{status}</Badge>;
  };

  const renderFormFields = () => {
    switch (contentType) {
      case 'homepage_hero':
        return (
          <div>
            <Label>Context/Season</Label>
            <Select value={formData.context} onValueChange={v => setFormData({...formData, context: v})}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="general">General</SelectItem>
                <SelectItem value="new_year">New Year Sales</SelectItem>
                <SelectItem value="valentines">Valentine's Day</SelectItem>
                <SelectItem value="back_to_school">Back to School</SelectItem>
                <SelectItem value="black_friday">Black Friday</SelectItem>
                <SelectItem value="christmas">Christmas</SelectItem>
                <SelectItem value="jamhuri_day">Jamhuri Day</SelectItem>
              </SelectContent>
            </Select>
          </div>
        );
      case 'category_description':
        return (
          <>
            <div>
              <Label>Category ID</Label>
              <Input 
                value={formData.categoryId} 
                onChange={e => setFormData({...formData, categoryId: e.target.value})}
                placeholder="e.g., laptops"
              />
            </div>
            <div>
              <Label>Category Name</Label>
              <Input 
                value={formData.categoryName} 
                onChange={e => setFormData({...formData, categoryName: e.target.value})}
                placeholder="e.g., Laptops & Notebooks"
              />
            </div>
          </>
        );
      case 'product_description':
        return (
          <div>
            <Label>Product ID</Label>
            <Input 
              value={formData.productId} 
              onChange={e => setFormData({...formData, productId: e.target.value})}
              placeholder="e.g., prod_reflap001"
            />
          </div>
        );
      case 'promotional_banner':
        return (
          <>
            <div>
              <Label>Promotion Type</Label>
              <Input 
                value={formData.promoType} 
                onChange={e => setFormData({...formData, promoType: e.target.value})}
                placeholder="e.g., Flash Sale, Weekend Deal"
              />
            </div>
            <div>
              <Label>Target Products/Categories</Label>
              <Input 
                value={formData.targets} 
                onChange={e => setFormData({...formData, targets: e.target.value})}
                placeholder="e.g., All Laptops, Refurbished Items"
              />
            </div>
            <div>
              <Label>Offer Details</Label>
              <Input 
                value={formData.offer} 
                onChange={e => setFormData({...formData, offer: e.target.value})}
                placeholder="e.g., Up to 30% off"
              />
            </div>
          </>
        );
      case 'seo_meta':
        return (
          <>
            <div>
              <Label>Page Name</Label>
              <Input 
                value={formData.pageName} 
                onChange={e => setFormData({...formData, pageName: e.target.value})}
                placeholder="e.g., Products, About Us"
              />
            </div>
            <div>
              <Label>Content Summary</Label>
              <Textarea 
                value={formData.contentSummary} 
                onChange={e => setFormData({...formData, contentSummary: e.target.value})}
                placeholder="Brief description of the page content..."
                rows={3}
              />
            </div>
          </>
        );
      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-accent" />
            AI Content Engine
          </h2>
          <p className="text-muted-foreground">Generate and manage AI-powered content</p>
        </div>
        <Button onClick={handleTriggerDaily} variant="outline">
          <Calendar className="w-4 h-4 mr-2" />
          Run Daily Updates
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="generate"><Wand2 className="w-4 h-4 mr-1" />Generate</TabsTrigger>
          <TabsTrigger value="pending">
            <Clock className="w-4 h-4 mr-1" />
            Pending ({pendingContent.length})
          </TabsTrigger>
          <TabsTrigger value="history"><FileText className="w-4 h-4 mr-1" />History</TabsTrigger>
        </TabsList>

        {/* Generate Tab */}
        <TabsContent value="generate">
          <div className="grid lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Generate Content</CardTitle>
                <CardDescription>Select content type and provide details</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Content Type</Label>
                  <Select value={contentType} onValueChange={setContentType}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="homepage_hero">Homepage Hero</SelectItem>
                      <SelectItem value="category_description">Category Description</SelectItem>
                      <SelectItem value="product_description">Product Description</SelectItem>
                      <SelectItem value="promotional_banner">Promotional Banner</SelectItem>
                      <SelectItem value="seo_meta">SEO Metadata</SelectItem>
                      <SelectItem value="about_page">About Page</SelectItem>
                      <SelectItem value="faq">FAQ Content</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                {renderFormFields()}
                
                <Button 
                  onClick={handleGenerate} 
                  disabled={loading}
                  className="w-full btn-primary"
                >
                  {loading ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4 mr-2" />
                      Generate with AI
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>

            {/* Preview */}
            {generatedPreview && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Eye className="w-5 h-5" />
                    Generated Preview
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="bg-secondary/30 p-4 rounded-lg mb-4">
                    <pre className="whitespace-pre-wrap text-sm overflow-auto max-h-64">
                      {typeof generatedPreview.generated_content === 'string' 
                        ? generatedPreview.generated_content 
                        : JSON.stringify(generatedPreview.generated_content, null, 2)}
                    </pre>
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      size="sm" 
                      onClick={() => handleApprove(generatedPreview.content_id)}
                      className="bg-green-600 hover:bg-green-700"
                    >
                      <Check className="w-4 h-4 mr-1" />
                      Approve
                    </Button>
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => handleReject(generatedPreview.content_id)}
                    >
                      <X className="w-4 h-4 mr-1" />
                      Reject
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        {/* Pending Tab */}
        <TabsContent value="pending">
          <Card>
            <CardHeader>
              <CardTitle>Pending Approval</CardTitle>
              <CardDescription>Review and approve generated content</CardDescription>
            </CardHeader>
            <CardContent>
              {pendingContent.length === 0 ? (
                <p className="text-center py-8 text-muted-foreground">No pending content</p>
              ) : (
                <div className="space-y-4">
                  {pendingContent.map(content => (
                    <div key={content.content_id} className="border rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <Tag className="w-4 h-4" />
                          <span className="font-medium capitalize">
                            {content.content_type.replace(/_/g, ' ')}
                          </span>
                          {getStatusBadge(content.status)}
                        </div>
                        <span className="text-xs text-muted-foreground">
                          {new Date(content.created_at).toLocaleString()}
                        </span>
                      </div>
                      <pre className="bg-secondary/30 p-3 rounded text-sm overflow-auto max-h-32 mb-3">
                        {typeof content.generated_content === 'string'
                          ? content.generated_content.slice(0, 300) + '...'
                          : JSON.stringify(content.generated_content, null, 2).slice(0, 300) + '...'}
                      </pre>
                      <div className="flex gap-2">
                        <Button size="sm" onClick={() => handleApprove(content.content_id)}>
                          <Check className="w-4 h-4 mr-1" />
                          Approve
                        </Button>
                        <Button size="sm" onClick={() => handlePublish(content.content_id)} variant="secondary">
                          <Upload className="w-4 h-4 mr-1" />
                          Approve & Publish
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => handleReject(content.content_id)}>
                          <X className="w-4 h-4 mr-1" />
                          Reject
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* History Tab */}
        <TabsContent value="history">
          <Card>
            <CardHeader>
              <CardTitle>Content History</CardTitle>
              <CardDescription>All generated content with status</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left p-3">Type</th>
                      <th className="text-left p-3">Status</th>
                      <th className="text-left p-3 hidden md:table-cell">Created</th>
                      <th className="text-left p-3">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {contentHistory.map(content => (
                      <tr key={content.content_id} className="border-b">
                        <td className="p-3 capitalize">{content.content_type.replace(/_/g, ' ')}</td>
                        <td className="p-3">{getStatusBadge(content.status)}</td>
                        <td className="p-3 hidden md:table-cell">
                          {new Date(content.created_at).toLocaleDateString()}
                        </td>
                        <td className="p-3">
                          <div className="flex gap-1">
                            {content.status === 'approved' && (
                              <Button size="sm" variant="outline" onClick={() => handlePublish(content.content_id)}>
                                <Upload className="w-3 h-3" />
                              </Button>
                            )}
                            {content.status === 'published' && (
                              <Button size="sm" variant="outline" onClick={() => aiContentAPI.revertContent(content.content_id)}>
                                <RotateCcw className="w-3 h-3" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AIContentManager;
